import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { apiRequest } from "../../helpers/api";
import { Skeleton, SkeletonRegion } from "../../components/skeleton/Skeleton";
import { showError, showSuccess } from "../../components/ToastNotifications";
import { useAdminAccess } from "../AdminAccessContext";
import { dateTime, relativeTime } from "./userFormatting";
import "./AdminManagement.css";
import "./UserQuickView.css";
import "./ContactSubmissions.css";

export const statusLabels = { new: "New", in_progress: "In progress", resolved: "Resolved" };
export const kindLabels = { contact: "Contact", demo: "Demo request" };
const utmLabels = [["utm_source", "UTM source"], ["utm_medium", "UTM medium"], ["utm_campaign", "UTM campaign"], ["utm_term", "UTM term"], ["utm_content", "UTM content"]];
const MAX_NOTE = 2000;

const Detail = ({ label, children }) => <div><dt>{label}</dt><dd>{children || "—"}</dd></div>;
const Card = ({ title, icon, children }) => (
  <article className="admin-management-card quick-view-card">
    <header><h3><i className={`ti ${icon}`} aria-hidden="true" />{title}</h3></header>
    {children}
  </article>
);
const Spinner = () => <span className="admin-management-spinner" aria-hidden="true" />;

const ManageCard = ({ submission, assignees, onSave }) => {
  const { can } = useAdminAccess();
  const canStatus = can("admin.contact_submissions.change_status");
  const canAssign = can("admin.contact_submissions.edit");
  const [saving, setSaving] = useState(null);
  const save = async (field, body) => {
    setSaving(field);
    await onSave(body);
    setSaving(null);
  };
  // Someone who has since lost access stays listed while they hold the inquiry.
  const assignee = submission.assignee;
  const options = assignee && !assignees.some((admin) => admin.id === assignee.id) ? [assignee, ...assignees] : assignees;
  return (
    <Card title="Manage" icon="ti-adjustments">
      <div className="admin-management-form quick-view-form">
        <label>Status
          <select value={submission.status} disabled={!canStatus || Boolean(saving)} onChange={(event) => save("status", { status: event.target.value })}>
            {Object.entries(statusLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </label>
        <label>Assigned to
          <select value={assignee ? String(assignee.id) : ""} disabled={!canAssign || Boolean(saving)} onChange={(event) => save("assignedTo", { assignedTo: event.target.value ? Number(event.target.value) : null })}>
            <option value="">Unassigned</option>
            {options.map((admin) => <option key={admin.id} value={String(admin.id)}>{admin.name}</option>)}
          </select>
        </label>
        {saving && <p className="quick-view-note is-wide" role="status">Saving…</p>}
        {submission.resolvedAt && !saving && <p className="quick-view-note is-wide">Resolved {dateTime(submission.resolvedAt)}</p>}
        {!canStatus && !canAssign && <p className="quick-view-note is-wide">Your role can view this inquiry but not change it.</p>}
      </div>
    </Card>
  );
};

const NotesCard = ({ submission, onAdded }) => {
  const { can } = useAdminAccess();
  const canEdit = can("admin.contact_submissions.edit");
  const [body, setBody] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    if (await onAdded(body.trim())) setBody("");
    setSaving(false);
  };
  return (
    <Card title="Internal notes" icon="ti-notes">
      {canEdit && <form className="contact-note-form" onSubmit={submit}>
        <label className="visually-hidden" htmlFor="contact-note-body">Add an internal note</label>
        <textarea id="contact-note-body" value={body} maxLength={MAX_NOTE} placeholder="Add a note for your team. The sender never sees this." onChange={(event) => setBody(event.target.value)} disabled={saving} />
        <div className="admin-management-form-actions">
          <button type="submit" className="admin-management-button primary" disabled={saving || !body.trim()}>{saving ? <><Spinner /> Adding…</> : "Add note"}</button>
        </div>
      </form>}
      {submission.notes.length ? <ul className="contact-notes">{submission.notes.map((note) => (
        <li key={note.id}>
          <p>{note.body}</p>
          <small>{note.author?.name || "Former administrator"} · <time dateTime={note.createdAt} title={dateTime(note.createdAt)}>{relativeTime(note.createdAt)}</time></small>
        </li>
      ))}</ul> : <p className="quick-view-note">No notes yet.</p>}
    </Card>
  );
};

const ContactSubmissionQuickView = ({ submissionId, assignees, onClose, onChanged }) => {
  const { can } = useAdminAccess();
  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [deleting, setDeleting] = useState(false);
  const panelRef = useRef(null);
  const titleId = useId();
  const request = useRef(0);
  const url = `/api/admin/contact-submissions/${encodeURIComponent(submissionId)}`;

  const load = useCallback(async () => {
    const requestId = ++request.current;
    setLoading(true);
    setError("");
    try {
      const result = await apiRequest(url);
      if (requestId === request.current) setSubmission(result.submission);
    } catch (requestError) {
      if (requestId === request.current) setError(requestError.message || "Unable to load this submission.");
    } finally {
      if (requestId === request.current) setLoading(false);
    }
  }, [url]);

  useEffect(() => {
    load();
    return () => { request.current += 1; };
  }, [load]);

  // Runs once per open drawer: focus moves in, Escape closes, and focus returns to where it was on close.
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    const previous = document.activeElement;
    panelRef.current?.focus();
    const onKey = (event) => { if (event.key === "Escape") closeRef.current(); };
    window.addEventListener("keydown", onKey);
    document.body.classList.add("quick-view-open");
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.classList.remove("quick-view-open");
      if (previous && typeof previous.focus === "function") previous.focus();
    };
  }, []);

  const save = async (body) => {
    try {
      const result = await apiRequest(url, { method: "PATCH", body: JSON.stringify(body) });
      setSubmission(result.submission);
      showSuccess(result.message);
      onChanged();
    } catch (requestError) {
      showError(requestError.message || "Unable to update this submission.");
    }
  };

  const addNote = async (body) => {
    try {
      const result = await apiRequest(`${url}/notes`, { method: "POST", body: JSON.stringify({ body }) });
      setSubmission(result.submission);
      showSuccess(result.message);
      return true;
    } catch (requestError) {
      showError(requestError.message || "Unable to add the note.");
      return false;
    }
  };

  const remove = async () => {
    if (!window.confirm(`Permanently delete this inquiry from ${submission.name}, including its notes? This can't be undone.`)) return;
    setDeleting(true);
    try {
      const result = await apiRequest(url, { method: "DELETE" });
      showSuccess(result.message);
      onChanged();
      onClose();
    } catch (requestError) {
      showError(requestError.message || "Unable to delete this submission.");
      setDeleting(false);
    }
  };

  const utm = submission?.utm || {};
  // Portalled to <body> so page entrance animations (which transform the page's children) can't shift the drawer.
  return createPortal(
    <div className="quick-view-root">
      <button type="button" className="quick-view-scrim" aria-label="Close quick view" onClick={onClose} tabIndex={-1} />
      <aside ref={panelRef} className="quick-view-panel contact-quick-view" role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1}>
        <header className="quick-view-header">
          <div className="quick-view-identity">
            <span className="admin-detail-avatar" aria-hidden="true">{submission ? submission.name.slice(0, 1).toUpperCase() : <i className="ti ti-mail" />}</span>
            <div>
              <h2 id={titleId}>{submission ? submission.name : loading ? "Loading submission…" : "Submission"}</h2>
              {submission && <p>{submission.email}</p>}
              {submission && <div className="quick-view-badges">
                <span className={`admin-status-badge contact-status is-${submission.status}`}>{statusLabels[submission.status]}</span>
                <span className="admin-user-role">{kindLabels[submission.kind]}</span>
                <span className="quick-view-muted">Submitted {dateTime(submission.createdAt)}</span>
              </div>}
            </div>
          </div>
          <div className="quick-view-header-actions">
            {submission && can("admin.contact_submissions.delete") && <button type="button" className="admin-management-button danger" onClick={remove} disabled={deleting}>{deleting ? <><Spinner /> Deleting…</> : <><i className="ti ti-trash" aria-hidden="true" /> <span>Delete</span></>}</button>}
            <button type="button" className="quick-view-icon-button" onClick={load} disabled={loading} aria-label="Refresh"><i className={`ti ti-refresh${loading && submission ? " is-spinning" : ""}`} aria-hidden="true" /></button>
            <button type="button" className="quick-view-icon-button" onClick={onClose} aria-label="Close quick view"><i className="ti ti-x" aria-hidden="true" /></button>
          </div>
        </header>

        <div className="quick-view-body">
          {error && <div className="admin-management-error" role="alert">{error} <button type="button" onClick={load}>Retry</button></div>}
          {!submission && loading && <SkeletonRegion label="Loading submission"><Skeleton height={140} radius={10} /><Skeleton height={120} radius={10} /></SkeletonRegion>}
          {submission && <div className="quick-view-stack">
            <Card title="Message" icon="ti-message">
              {submission.message ? <p className="contact-message">{submission.message}</p> : <p className="quick-view-note">No message was included.</p>}
            </Card>
            <div className="admin-management-grid">
              <ManageCard submission={submission} assignees={assignees} onSave={save} />
              <Card title="Contact details" icon="ti-address-book">
                <dl className="admin-management-meta">
                  <Detail label="Name">{submission.name}</Detail>
                  <Detail label="Email"><a className="admin-inline-link" href={`mailto:${submission.email}`}>{submission.email}</a></Detail>
                  <Detail label="Company">{submission.company}</Detail>
                  <Detail label="Consent to contact">{`Given ${dateTime(submission.consentAt)}`}</Detail>
                </dl>
              </Card>
            </div>
            <div className="admin-management-grid">
              <NotesCard submission={submission} onAdded={addNote} />
              <Card title="Source" icon="ti-world">
                <dl className="admin-management-meta">
                  <Detail label="Page">{submission.sourcePath}</Detail>
                  <Detail label="Referrer">{submission.referrer}</Detail>
                  {utmLabels.filter(([key]) => utm[key]).map(([key, label]) => <Detail key={key} label={label}>{utm[key]}</Detail>)}
                </dl>
              </Card>
            </div>
          </div>}
        </div>
      </aside>
    </div>,
    document.body
  );
};

export default ContactSubmissionQuickView;
